<template>
	<Popover @open="onOpen" @close="onClose" v-bind="popoverProps" ref="popover">
		<slot />
		<template #content>
			<AccessibleSection
				name="location-map-popover"
				:tabOrder="tabOrder"
				:focusFirstItem="true"
				:trapped="true"
				ref="accessibleSection"
			>
				<div class="location-map-wrapper">
					<div class="location-map-container">
						<div ref="mapContainer" class="map-canvas"></div>
						<div v-if="isLoading" class="map-overlay map-loading">
							<div class="loading-spinner"></div>
						</div>
						<div v-else-if="loadError" class="map-overlay map-error">
							<div class="error-text">Failed to load map</div>
						</div>
					</div>
					<div
						v-if="!geocodeError"
						class="address-display"
						tabindex="-1"
						role="button"
						:aria-label="
							address || $t('chat_location_address_loading_placeholder')
						"
						@click="openInGoogleMaps"
					>
						<span v-if="address">{{ address }}</span>
						<span v-else class="address-loading">{{
							$t('chat_location_address_loading_placeholder')
						}}</span>
					</div>
				</div>
				<div v-if="!appcheckVerified" class="unverified-location">
					<CircleExclamationIcon type="solid" />
					<span>{{ $t('chat_location_unverified_location') }}</span>
				</div>
			</AccessibleSection>
		</template>
	</Popover>
</template>

<script>
// Module variables - shared across all LocationMap instances
let mapsLoaded = false
let mapsLoading = false
let googleMapsLoadError = null

async function loadGoogleMaps() {
	// Already loaded
	if (mapsLoaded) return { success: true, error: null }

	// Currently loading, wait for finish
	if (mapsLoading) {
		while (mapsLoading) {
			await new Promise((resolve) => setTimeout(resolve, 100))
		}
		return { success: mapsLoaded, error: googleMapsLoadError }
	}

	// Start loading
	mapsLoading = true
	googleMapsLoadError = null

	try {
		const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

		if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
			throw new Error('Google Maps API key not configured')
		}

		const existingScript = document.querySelector(
			'script[src*="maps.googleapis.com"]'
		)
		if (existingScript) {
			mapsLoaded = true
			mapsLoading = false
			return { success: true, error: null }
		}

		// Load google maps
		await new Promise((resolve, reject) => {
			// Create a unique callback name
			const callbackName = `__googleMapsCallback_${Date.now()}`
			window[callbackName] = () => {
				delete window[callbackName]
				resolve()
			}

			const script = document.createElement('script')
			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async&callback=${callbackName}`
			script.async = true

			script.onerror = () => {
				delete window[callbackName]
				reject(new Error('Failed to load Google Maps script'))
			}

			document.head.appendChild(script)
		})

		mapsLoaded = true
		mapsLoading = false
		return { success: true, error: null }
	} catch (error) {
		console.error('Error loading Google Maps:', error)
		googleMapsLoadError = error.message
		mapsLoading = false
		return { success: false, error: error.message }
	}
}
</script>

<script setup>
import { ref, computed, useTemplateRef, nextTick } from 'vue'
import Popover from './Popover.vue'
import AccessibleSection from './Accessible/AccessibleSection.vue'
import CircleExclamationIcon from '@/icons/CircleExclamation.vue'
import { useI18n } from 'vue-i18n'
import store, { getUser } from '@/store'
import {
	getTimeAgoShorthand,
	getInitials,
	getLocalDate,
	safeUrl,
} from '@/helpers/utils'

const { t } = useI18n()

const emit = defineEmits(['open', 'close'])
const props = defineProps({
	lat: { type: Number, required: true },
	long: { type: Number, required: true },
	accuracy: { type: Number, default: null },
	popoverProps: { type: Object, default: () => ({}) },
	appcheckVerified: { type: Boolean, default: false },
	userId: { type: [String, Number], default: null },
	createdAt: { type: [Date, String], default: null },
})

// Computed user info - only resolved when needed
const user = computed(() => (props.userId ? getUser(props.userId) : null))
const userName = computed(() => user.value?.fullname || null)
const userImage = computed(() => {
	if (!user.value) return null
	// Respect the "has a picture" flag — profilepicurl can be stale after a
	// user removes their picture. Fall back to initials (userInitials) instead.
	if (!user.value.profilepic) return null
	if (user.value.profilepicpath) {
		return store.userProfile(user.value)
	}
	return user.value.profilepicurl || null
})
const userInitials = computed(() => {
	if (!user.value) return ''
	return getInitials(user.value)
})

const popover = useTemplateRef('popover')
const accessibleSection = useTemplateRef('accessibleSection')
const mapContainer = ref(null)
const mapInstance = ref(null)
const isLoading = ref(false)
const loadError = ref(null)
const address = ref('')
const geocodeError = ref(false)

const tabOrder = computed(() => [
	{
		css: '.address-display',
		clickOnEnter: false,
		force: true,
	},
	{
		all: '.map-canvas a[href*="maps.google.com"], .map-canvas a[href*="maps.app.goo.gl"]',
		clickOnEnter: true,
	},
	{
		all: '.gmnoprint a[href]',
		clickOnEnter: true,
	},
])

async function initMap() {
	isLoading.value = true
	loadError.value = null

	const result = await loadGoogleMaps()

	if (!result.success) {
		loadError.value = result.error
		isLoading.value = false
		return
	}

	await nextTick()

	if (!mapContainer.value) {
		loadError.value = 'Map container not available'
		isLoading.value = false
		return
	}

	try {
		// Creating our map here
		const position = { lat: props.lat, lng: props.long }

		mapInstance.value = new google.maps.Map(mapContainer.value, {
			center: position,
			zoom: 15, // Initial zoom, will be adjusted by fitBounds
			mapTypeControl: false,
			streetViewControl: false,
			fullscreenControl: false,
			mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined,
		})

		// Add marker
		if (user.value) {
			// Custom overlay marker with profile image or initials
			class CustomMarkerOverlay extends google.maps.OverlayView {
				constructor(position, imageUrl, initials, name, timeAgo) {
					super()
					this.position = position
					// Scheme-allowlist at the boundary: profilepicurl is arbitrary server
					// data and store.userProfile() passes non-pronto.io URLs through
					// untouched, so a javascript: scheme can otherwise reach src. Anything
					// outside http/https/blob becomes undefined and we render initials,
					// exactly as for a user with no picture.
					this.imageUrl = safeUrl(imageUrl)
					this.initials = initials
					this.name = name
					this.timeAgo = timeAgo
					this.div = null
				}

				onAdd() {
					// Built imperatively, never with innerHTML: name, initials and the
					// image URL are unprocessed user/server data, so they may only be set
					// as text or as an already-allowlisted attribute value.
					this.div = document.createElement('div')
					this.div.className = 'custom-map-marker'

					const avatar = document.createElement('div')
					avatar.className = 'marker-avatar'
					if (this.imageUrl) {
						const img = document.createElement('img')
						img.src = this.imageUrl
						img.alt = this.name || 'User'
						avatar.appendChild(img)
					} else {
						const initials = document.createElement('span')
						initials.className = 'marker-initials'
						initials.textContent = this.initials
						avatar.appendChild(initials)
					}
					this.div.appendChild(avatar)

					const tail = document.createElement('span')
					tail.className = 'marker-tail'
					this.div.appendChild(tail)

					const label = document.createElement('span')
					label.className = 'marker-label'
					if (this.name) {
						const name = document.createElement('span')
						name.className = 'marker-name'
						name.textContent = this.name
						label.appendChild(name)
					}
					if (this.timeAgo) {
						const time = document.createElement('span')
						time.className = 'marker-time'
						time.textContent = this.timeAgo
						label.appendChild(time)
					}
					this.div.appendChild(label)

					const panes = this.getPanes()
					panes.overlayMouseTarget.appendChild(this.div)
				}

				draw() {
					const overlayProjection = this.getProjection()
					const pos = overlayProjection.fromLatLngToDivPixel(this.position)
					if (this.div) {
						this.div.style.left = pos.x + 'px'
						this.div.style.top = pos.y + 'px'
					}
				}

				onRemove() {
					if (this.div) {
						this.div.parentNode.removeChild(this.div)
						this.div = null
					}
				}
			}

			const timeAgo = props.createdAt
				? t('common_date_relative', [
						getTimeAgoShorthand(getLocalDate(props.createdAt)),
				  ])
				: null
			const overlay = new CustomMarkerOverlay(
				new google.maps.LatLng(position.lat, position.lng),
				userImage.value,
				userInitials.value,
				userName.value,
				timeAgo
			)
			overlay.setMap(mapInstance.value)
		} else {
			// Default marker
			new google.maps.Marker({
				position: position,
				map: mapInstance.value,
				title: 'Location',
			})
		}

		// Attempted to match mobile as much as possible here, but fitBounds differs from MapKit.
		const horizontalAccuracy = props.accuracy ?? 0 // meters from server

		// MapKit: region span (width/height) in meters, min 200m
		const minSpanMeters = 200
		const regionSpanMeters = Math.max(horizontalAccuracy * 2, minSpanMeters)

		// Google fitBounds works off bounds (diameter), so use radius = span / 2
		const baseFitRadiusMeters = regionSpanMeters / 2

		// zoom tuning knob (smaller = more zoomed in)
		const zoomTightness = 0.85

		const fitRadiusMeters = baseFitRadiusMeters * zoomTightness

		// Draw TRUE accuracy circle (do not clamp this)
		if (horizontalAccuracy > 0) {
			new google.maps.Circle({
				map: mapInstance.value,
				center: position,
				radius: horizontalAccuracy,
				strokeColor: '#49a4da',
				strokeWeight: 2,
				fillColor: '#49a4da',
				fillOpacity: 0.2,
			})
		}

		// Fit map to the (possibly capped) region
		const fitCircle = new google.maps.Circle({
			center: position,
			radius: fitRadiusMeters,
		})

		mapInstance.value.fitBounds(fitCircle.getBounds(), 0)

		// Use reverse geocoder to get address
		const geocoder = new google.maps.Geocoder()
		geocoder.geocode({ location: position }, (results, status) => {
			if (status === 'OK' && results && results[0]) {
				address.value = results[0].formatted_address
			} else {
				geocodeError.value = true
				console.warn(
					'Geocoding failed - enable Geocoding API in Google Cloud Console. Status:',
					status
				)
			}
		})

		isLoading.value = false
		setTimeout(() => {
			// accessibility
			accessibleSection.value?.requeryTabList?.()
		}, 500)
	} catch (error) {
		console.error('Error initializing map:', error)
		loadError.value = error.message
		isLoading.value = false
	}
}

async function onOpen() {
	emit('open', true)
	await nextTick()
	if (!mapInstance.value) {
		await initMap()
	}
	await nextTick()
	accessibleSection.value?.focusMe?.()
}

function onClose() {
	emit('close', false)
	mapInstance.value = null
	address.value = ''
	geocodeError.value = false
}

function openMap() {
	popover.value?.openPopover()
}

function closeMap() {
	popover.value?.closePopover()
}

function openInGoogleMaps() {
	window.open(
		`https://www.google.com/maps?q=${props.lat},${props.long}`,
		'_blank',
		'noopener,noreferrer'
	)
}

defineExpose({
	openMap,
	closeMap,
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.location-map-wrapper {
	width: 300px;
	margin-top: 8px;
	background-color: var(--neutral-0);
	@include radius;
	box-shadow: 0 8px 64px 0 rgba(0, 0, 0, 0.15);
	overflow: hidden;

	&:last-child {
		margin-bottom: 8px;
	}
}

.location-map-container {
	width: 100%;
	height: 200px;
	position: relative;

	.map-canvas {
		width: 100%;
		height: 100%;
	}

	.map-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 12px;
		background-color: var(--neutral-0);
		z-index: 1;
	}

	.loading-spinner {
		width: 32px;
		height: 32px;
		border: 3px solid var(--neutral-200);
		border-top-color: var(--teal-300);
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	.loading-text {
		font-size: 14px;
		color: var(--neutral-600);
	}

	.error-text {
		font-size: 14px;
		color: var(--red-500);
	}
}

.address-display {
	padding: 12px 16px;
	font-family: Avenir-Medium;
	font-size: 13px;
	color: var(--neutral-800);
	border-top: 1px solid var(--neutral-100);
	background-color: var(--neutral-0);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	cursor: pointer;

	.address-loading {
		color: var(--neutral-500);
		font-style: italic;
	}
}

.unverified-location {
	height: 24px;
	margin-top: 3px;
	margin-bottom: 8px;
	margin-left: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	font-family: Avenir-Heavy;
	font-size: 11px;
	color: var(--blue-400);
	width: max-content;
	background-color: var(--neutral-0);
	border-radius: 6px;
	padding: 0 6px;
	.pronto-icon {
		font-size: 12px;
	}
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.address-display {
		background-color: var(--neutral-300);
	}
	.unverified-location {
		background-color: var(--blue-200);
		color: var(--neutral-800);
	}
}

// Custom marker styles (must be global since marker is in Google Maps DOM)
.custom-map-marker {
	position: absolute;
	transform: translate(-50%, -90%);
	display: flex;
	flex-direction: column;
	align-items: center;
	filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));

	.marker-avatar {
		width: 44px;
		height: 44px;
		border-radius: 50%;
		border: 3px solid #fff;
		background-color: var(--neutral-500);
		overflow: hidden;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.marker-initials {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		font-family: Avenir-Medium, sans-serif;
		font-size: 18px;
		text-transform: uppercase;
	}

	.marker-tail {
		border-left: 5px solid transparent;
		border-right: 5px solid transparent;
		border-top: 8px solid #fff;
	}

	.marker-label {
		position: absolute;
		top: 100%;
		margin-top: 12px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2px;
	}

	.marker-name,
	.marker-time {
		font-family: Avenir-Black;
		color: var(--neutral-800);
		white-space: nowrap;

		letter-spacing: 0.05em;
		text-shadow: -1px 0 0 #fff, 1px 0 0 #fff, 0 -1px 0 #fff, 0 1px 0 #fff,
			-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;
	}

	.marker-name {
		font-size: 14px;
	}

	.marker-time {
		font-size: 12px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.custom-map-marker {
		.marker-name {
			color: var(--neutral-0) !important;
		}
		.marker-time {
			color: var(--neutral-0) !important;
		}
	}
}
</style>
